import md5 from 'md5';
import { isServer } from './utils.js';

const processEncryption = (encryption, fingerprint, isEncrypt, data, tsInSeconds, token = '') => {
    let result = data;
    if (encryption) {
        if (isEncrypt) {
            result = handleEncryption(data, tsInSeconds, token, fingerprint);
        }
        else {
            result = handleDecryption(data, tsInSeconds, token, fingerprint);
        }
    }
    return result;
};
const handleEncryption = (body, tsInSeconds, reqToken = '', fingerprint) => {
    const token = reqToken && reqToken !== null ? reqToken : generateToken(tsInSeconds, true, fingerprint);
    return getEncryptedRequestBody(body, token);
};
const handleDecryption = (response, tsInSeconds, resKey = '', fingerprint) => {
    const key = resKey && resKey !== null ? resKey : generateToken(tsInSeconds, false, fingerprint);
    return JSON.parse(getDecryptedResponseBody(response, key));
};
const paramsToEncodedBody = (params) => {
    let urlEncodedBody = '';
    Object.keys(params).forEach((key, index) => {
        const delimiter = index === 0 ? '' : '&';
        urlEncodedBody += `${delimiter}${key}=${params[key]}`;
    });
    return urlEncodedBody;
};
/**
 * @description Generate encryption/decryption keys
 * @param {number} timestamp The current timestamp used in auth keys
 * @param {boolean} isAuth Depend on the timestmap to genarate the auth keys
 * @param {string} udid The fingerprint used to generate keys
 * @returns {string}
 */
const generateToken = (timestamp, isAuth, udid) => {
    const lowFingerprint = udid === null || udid === void 0 ? void 0 : udid.toLowerCase();
    const hashTimes = stringToNumber(lowFingerprint) % (isAuth ? 7 : 13);
    let token = '-Jlfi6:CFND;bpKs;svX]dj@';
    for (let i = 0; i <= hashTimes; i++) {
        token = md5(token + lowFingerprint + timestamp.toString());
    }
    return token;
};
/**
 * @description Convert base64 to Unit8Array
 * @param {string} input The base64 input
 * @returns {Uint8Array}
 */
const b64ToBytes = (input) => {
    const base64Decoded = atob(input);
    const length = base64Decoded.length;
    const result = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        result[i] = base64Decoded.charCodeAt(i);
    }
    return result;
};
/**
 * @description Gets the total amount of char code
 * @param {string} inString The string to calc the charcodes from
 * @returns {number}
 */
const stringToNumber = (inString) => {
    let total = 0;
    for (let i = 0; i < inString.length; i++) {
        total += inString.charCodeAt(i);
    }
    return total;
};
/**
 * @description Slices an array
 * @param {Uint8Array} array The target array
 * @param {number} start The start index to be slice from
 * @param {number} len The amount of elements to be slices
 * @returns {Uint8Array}
 */
const getArraySlice = (array, start, len) => {
    const sodium = isServer ? global.sodium : window.sodium;
    const nonceLength = sodium.crypto_aead_chacha20poly1305_NPUBBYTES;
    const adLength = sodium.crypto_aead_chacha20poly1305_ietf_NPUBBYTES;
    const hashLength = 2;
    const numberOfIndicesToDiscard = nonceLength + adLength + hashLength;
    const length = len || array.length - numberOfIndicesToDiscard;
    const list = [];
    let pos = start;
    for (let i = 0; i < length; i++) {
        list[i] = array[pos++];
    }
    return new Uint8Array(list);
};
/**
 * @description To encrypt a request body using a key
 * @param {interfaces.IAuthenticateRequest | interfaces.IGetDownloadRequest} body The js object to be encrypted
 * @param {string} key The encryption key
 * @returns {Uint8Array}
 */
const getEncryptedRequestBody = (body, key) => {
    const sodium = isServer ? global.sodium : window.sodium;
    const fflate = isServer ? global.fflate : window.fflate;
    const urlEncodedBody = fflate.gzipSync(fflate.strToU8(paramsToEncodedBody(body)));
    const nonce = sodium.randombytes_buf(sodium.crypto_aead_chacha20poly1305_NPUBBYTES);
    const AD = sodium.randombytes_buf(sodium.crypto_aead_chacha20poly1305_ietf_NPUBBYTES);
    const encryptedBody = sodium.crypto_aead_chacha20poly1305_encrypt(urlEncodedBody, AD, null, nonce, key);
    const buffer = new Uint8Array(nonce.length + AD.length + encryptedBody.length + 2);
    buffer[0] = 35;
    buffer[1] = 35;
    let pos = 2;
    for (let i = 0; i < nonce.length; i++) {
        buffer[pos++] = nonce[i];
    }
    for (let i = 0; i < AD.length; i++) {
        buffer[pos++] = AD[i];
    }
    for (let i = 0; i < encryptedBody.length; i++) {
        buffer[pos++] = encryptedBody[i];
    }
    return buffer;
};
/**
 * @description To decrypt a response body using keys
 * @param encryptedBody The encrypted string
 * @param key The key to decrypt the string by
 * @returns {string}
 */
const getDecryptedResponseBody = (encryptedBodyResponse, key) => {
    const sodium = isServer ? global.sodium : window.sodium;
    const fflate = isServer ? global.fflate : window.fflate;
    const encryptedBody = (encryptedBodyResponse === null || encryptedBodyResponse === void 0 ? void 0 : encryptedBodyResponse.reply) || encryptedBodyResponse;
    const encryptedResponseArray = b64ToBytes(encryptedBody);
    let secondHashIndex = 0;
    let count = 0;
    for (let i = 0; i < encryptedResponseArray.length; i++) {
        if (encryptedResponseArray[i] === 35) {
            count++;
        }
        if (count === 2) {
            secondHashIndex = i;
            break;
        }
    }
    const nonceStart = secondHashIndex + 1;
    const adStart = nonceStart + 8;
    const adEndNonInclusive = adStart + 12;
    const encryptedBodyStart = adEndNonInclusive;
    const nonceArray = getArraySlice(encryptedResponseArray, nonceStart, 8);
    const adArray = getArraySlice(encryptedResponseArray, adStart, 12);
    const encryptedBodyArray = getArraySlice(encryptedResponseArray, encryptedBodyStart);
    let decryptedBody = sodium.crypto_aead_chacha20poly1305_decrypt(null, encryptedBodyArray, adArray, nonceArray, key);
    decryptedBody = fflate.gunzipSync(decryptedBody);
    const bodyUtf8 = new TextDecoder('utf-8').decode(decryptedBody);
    return bodyUtf8;
};

export { b64ToBytes, generateToken, getArraySlice, getDecryptedResponseBody, getEncryptedRequestBody, handleDecryption, handleEncryption, paramsToEncodedBody, processEncryption, stringToNumber };
//# sourceMappingURL=encryption.js.map
